import {extractCoordinatesFromWKT} from '@app/utils/wkt-parser';
import {isPointInMultiPolygon} from '@app/utils/polygons.utils';

/** Degrees. Tight enough that a point clearly outside a field is rejected. */
const BOUNDARY_EPSILON = 1e-8;

/**
 * True when (lat, lng) is inside a WKT polygon or multipolygon, or on its boundary.
 * Holes are outside. `wkt` must already be WKT (lng lat), not raw GeoJSON.
 */
export function isCoordinateInsidePolygonWkt(
  lat: number,
  lng: number,
  wkt: string | null | undefined,
): boolean {
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || !wkt?.trim()) {
    return false;
  }

  const polygons = extractCoordinatesFromWKT(wkt);
  if (!polygons.length) {
    return false;
  }

  const point: [number, number] = [lat, lng];
  return isPointInMultiPolygon(point, polygons) || isPointOnMultiPolygonBoundary(point, polygons);
}

function isPointOnMultiPolygonBoundary(
  point: [number, number],
  polygons: Array<Array<Array<[number, number]>>>,
): boolean {
  for (const rings of polygons) {
    for (const ring of rings) {
      if (isPointOnRingBoundary(point, ring)) {
        return true;
      }
    }
  }
  return false;
}

function isPointOnRingBoundary(
  point: [number, number],
  ring: Array<[number, number]>,
): boolean {
  if (!ring || ring.length < 2) {
    return false;
  }

  for (let i = 0; i < ring.length; i++) {
    const next = ring[(i + 1) % ring.length];
    if (isPointOnSegment(point, ring[i], next)) {
      return true;
    }
  }
  return false;
}

function isPointOnSegment(
  point: [number, number],
  start: [number, number],
  end: [number, number],
): boolean {
  const [py, px] = point;
  const [ay, ax] = start;
  const [by, bx] = end;
  if (ay === by && ax === bx) {
    return Math.abs(py - ay) <= BOUNDARY_EPSILON && Math.abs(px - ax) <= BOUNDARY_EPSILON;
  }
  const cross = (py - ay) * (bx - ax) - (px - ax) * (by - ay);
  if (Math.abs(cross) > BOUNDARY_EPSILON) {
    return false;
  }

  const dot = (px - ax) * (bx - ax) + (py - ay) * (by - ay);
  if (dot < -BOUNDARY_EPSILON) {
    return false;
  }

  const lengthSquared = (bx - ax) * (bx - ax) + (by - ay) * (by - ay);
  return dot - lengthSquared <= BOUNDARY_EPSILON;
}
